/**
 * window.EddShipping
 *
 * Shared, DOM-free date engine for the PDP shipping estimates. It is consumed by
 * both the delivery-strip custom element (assets/estimated-delivery-date.js) and
 * the shipping-options popup (assets/shipping-options-popup.js) so the two always
 * agree on the ship date and delivery-day math.
 *
 * All timing is Pacific Time (America/Los_Angeles) so the cutoff logic is
 * independent of the shopper's timezone and of Shopify full-page caching.
 *
 * Ship-date rule (per PRD, verified against the UAT table):
 *   Ship days are Tue-Fri (never Mon/Sun; Sat only when Saturday shipping is enabled)
 *   and never a federal holiday.
 *   Before the cutoff hour on a ship day -> ships today; otherwise the next ship day.
 * Delivery range: ship date + CALENDAR days (weekends count, holidays are skipped;
 *   Sundays are also skipped when Sunday exclusion is enabled).
 *
 * Config is passed in per call (never read from `this`) so the functions stay pure:
 *   { holidays: Set<string 'YYYY-MM-DD'>, saturdayShipping: bool, excludeSundays: bool }
 */
(function () {
  if (window.EddShipping) return;

  // Upper bound on the day-stepping loops below. Real ship/delivery windows span a
  // handful of days; this only guards against a pathological holiday list.
  var MAX_DAY_STEPS = 750;

  window.EddShipping = {
    MAX_DAY_STEPS: MAX_DAY_STEPS,

    // Current time as a Date whose LOCAL fields hold the Pacific-Time wall-clock
    // (auto-handles PST/PDT). Read the parts straight off Intl rather than parsing
    // a formatted string back through `new Date(...)`, which is fragile across ICU
    // versions (e.g. the U+202F narrow no-break space before AM/PM in ICU 72+ can
    // yield an Invalid Date).
    nowInPacific: function () {
      var parts = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Los_Angeles",
        year: "numeric",
        month: "numeric",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        second: "numeric",
        hour12: false,
      }).formatToParts(new Date());
      var f = {};
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type !== "literal") f[parts[i].type] = parts[i].value;
      }
      // hour12:false reports midnight as "24" in some engines; normalise to 0.
      var hour = parseInt(f.hour, 10) % 24;
      return new Date(
        parseInt(f.year, 10),
        parseInt(f.month, 10) - 1,
        parseInt(f.day, 10),
        hour,
        parseInt(f.minute, 10),
        parseInt(f.second, 10),
        0
      );
    },

    // Parse a data-attribute hour into a valid 0-24 integer, or a fallback.
    parseHour: function (value, fallback) {
      var h = parseInt(value, 10);
      return isFinite(h) ? h : fallback;
    },

    // Standard-engine config from an element's dataset flags. Shared so the trust
    // strip and the popup's Standard/Express/Overnight rows never drift apart.
    // Saturday shipping is a Peak Season feature, so it requires BOTH the master
    // peak-season toggle AND the Saturday toggle to be on.
    shipConfig: function (dataset, holidays) {
      return {
        holidays: holidays,
        saturdayShipping:
          dataset.peakEnabled === "true" && dataset.peakSaturday === "true",
        excludeSundays: dataset.excludeSundays === "true",
      };
    },

    // Date-only copy pinned to noon, avoiding any DST hour rollover during day math.
    dateOnly: function (d) {
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0, 0);
    },

    ymd: function (d) {
      var m = String(d.getMonth() + 1).padStart(2, "0");
      var day = String(d.getDate()).padStart(2, "0");
      return d.getFullYear() + "-" + m + "-" + day;
    },

    parseHolidays: function (json) {
      try {
        return new Set(JSON.parse(json || "[]"));
      } catch (e) {
        return new Set();
      }
    },

    isHoliday: function (holidays, d) {
      return holidays.has(this.ymd(d));
    },

    // Ship days: Tuesday-Friday only (Saturday when enabled), excluding federal holidays.
    isShipDay: function (d, cfg) {
      var day = d.getDay(); // Sun=0 ... Sat=6
      var shipsWeekday = day >= 2 && day <= 5; // Tue-Fri
      var shipsSaturday = cfg.saturdayShipping && day === 6; // Peak Season override
      return (shipsWeekday || shipsSaturday) && !this.isHoliday(cfg.holidays, d);
    },

    shipDate: function (now, cutoffHour, cfg) {
      var ship = this.dateOnly(now);
      var shipsToday = this.isShipDay(ship, cfg) && now.getHours() < cutoffHour;
      if (!shipsToday) {
        var steps = 0;
        do {
          ship.setDate(ship.getDate() + 1);
        } while (!this.isShipDay(ship, cfg) && ++steps < MAX_DAY_STEPS);
      }
      return ship;
    },

    // Calendar days forward from the ship date: weekends count and holidays are
    // skipped; Sundays are also skipped when Sunday exclusion is enabled.
    addDeliveryDays: function (start, n, cfg) {
      var d = new Date(start.getTime());
      var added = 0;
      var steps = 0;
      while (added < n && ++steps < MAX_DAY_STEPS) {
        d.setDate(d.getDate() + 1);
        if (this.isDeliveryDay(d, cfg)) added += 1;
      }
      return d;
    },

    // A day that can count toward, and be shown as, a delivery date.
    isDeliveryDay: function (d, cfg) {
      if (this.isHoliday(cfg.holidays, d)) return false;
      if (cfg.excludeSundays && d.getDay() === 0) return false;
      return true;
    },

    /* -----------------------------------------------------------------------
     * Expedited (Express / Overnight) rules — distinct from Standard.
     * Business days are Mon-Fri (Monday DOES ship, unlike Standard), excluding
     * federal holidays. During peak season Saturday also counts as a business
     * day via cfg.saturdayShipping. That toggle affects DISPATCH only; expedited
     * transit counts Mon-Fri, so Express/Overnight never arrive on a weekend. Ship today when it's
     * a business day before the cutoff, otherwise the next business day. Arrival
     * counts BUSINESS days, per the PRD.
     * --------------------------------------------------------------------- */
    // DISPATCH test for expedited: which days an order can leave the warehouse.
    // Mon-Fri, plus Saturday when peak-season Saturday shipping is on.
    isBusinessDay: function (cfg, d) {
      var day = d.getDay(); // Sun=0 ... Sat=6
      var isWeekday = day >= 1 && day <= 5; // Mon-Fri
      var isSaturday = cfg.saturdayShipping && day === 6; // peak-season override
      return (isWeekday || isSaturday) && !this.isHoliday(cfg.holidays, d);
    },

    // TRANSIT test for expedited: which days count toward an Express/Overnight
    // arrival. Mon-Fri minus holidays. Express and Overnight are NOT delivered on
    // Saturdays or Sundays, so neither can ever be an arrival date (Standard is
    // different - it counts calendar days and can land on a weekend).
    // cfg.saturdayShipping is deliberately not consulted: that toggle governs
    // DISPATCH only and must never change an arrival date.
    isTransitDay: function (cfg, d) {
      var day = d.getDay(); // Sun=0 ... Sat=6
      return day >= 1 && day <= 5 && !this.isHoliday(cfg.holidays, d);
    },

    expeditedShipDate: function (now, cutoffHour, cfg) {
      var ship = this.dateOnly(now);
      var shipsToday =
        this.isBusinessDay(cfg, ship) && now.getHours() < cutoffHour;
      if (!shipsToday) {
        var steps = 0;
        do {
          ship.setDate(ship.getDate() + 1);
        } while (!this.isBusinessDay(cfg, ship) && ++steps < MAX_DAY_STEPS);
      }
      return ship;
    },

    addBusinessDays: function (start, n, cfg) {
      var d = new Date(start.getTime());
      var added = 0;
      var steps = 0;
      while (added < n && ++steps < MAX_DAY_STEPS) {
        d.setDate(d.getDate() + 1);
        if (this.isTransitDay(cfg, d)) added += 1;
      }
      return d;
    },

    // Whether an expedited order placed right now could still ship today — the
    // condition for showing the "order by <cutoff> to ship today" sub-line.
    canShipTodayExpedited: function (now, cutoffHour, cfg) {
      return this.isBusinessDay(cfg, now) && now.getHours() < cutoffHour;
    },

    formatDate: function (d, withYear) {
      var opts = { weekday: "short", month: "short", day: "numeric" };
      if (withYear) opts.year = "numeric";
      return d.toLocaleDateString("en-US", opts);
    },
  };
})();
